import React, { useState, useEffect, useRef } from 'react';
import { Input, message } from 'antd';

/**
 * 统一年份输入组件
 *
 * 支持所有4种使用场景：DepartmentView、IndicatorPanel、EditableDataTable、EditableTargetTable
 *
 * @param {Object} props
 * @param {number|null} props.value - 当前年份值
 * @param {function} props.onChange - 值变更回调
 * @param {function} [props.onBlur] - 失焦回调
 * @param {function} [props.onPressEnter] - 回车回调
 * @param {function} [props.onKeyDown] - 键盘事件回调
 * @param {string} [props.placeholder] - 占位文本
 * @param {Object} [props.style] - 样式
 * @param {number} [props.min=1900] - 最小年份
 * @param {number} [props.max=2100] - 最大年份
 * @param {boolean} [props.autoFocus=false] - 自动聚焦
 * @param {string} [props.data-cell-key] - 表格内编辑标识
 * @param {'clamp'|'reject'} [props.validationMode='reject'] - 验证模式：clamp=静默钳制，reject=拒绝+消息提示
 * @param {'number'|'event'} [props.onChangeFormat='number'] - onChange回调格式
 * @param {boolean} [props.zeroPad=false] - 失焦时是否补零到4位
 */
const YearInput = ({
  value,
  onChange,
  onBlur,
  onPressEnter,
  onKeyDown,
  placeholder,
  style,
  min = 1900,
  max = 2100,
  autoFocus = false,
  'data-cell-key': dataCellKey,
  validationMode = 'reject',
  onChangeFormat = 'number',
  zeroPad = false,
}) => {
  const inputRef = useRef(null);
  const [localValue, setLocalValue] = useState(value != null ? String(value) : '');

  useEffect(() => {
    setLocalValue(value != null ? String(value) : '');
  }, [value]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const notifyChange = (numValue) => {
    if (onChange) {
      if (onChangeFormat === 'event') {
        onChange({ target: { value: numValue != null ? String(numValue) : '' } });
      } else {
        onChange(numValue);
      }
    }
  };

  const handleChange = (e) => {
    const raw = e.target.value;
    const numbersOnly = raw.replace(/\D/g, '').slice(0, 4);
    setLocalValue(numbersOnly);

    if (numbersOnly.length === 4) {
      const year = parseInt(numbersOnly, 10);
      if (year >= min && year <= max) {
        notifyChange(year);
      } else if (validationMode === 'clamp') {
        notifyChange(Math.min(Math.max(year, min), max));
      }
    } else if (numbersOnly === '') {
      notifyChange(null);
    }
  };

  const validateAndFormat = () => {
    if (!localValue) {
      setLocalValue(value != null ? String(value) : '');
      return;
    }
    const year = parseInt(localValue, 10);
    if (isNaN(year) || year < min || year > max) {
      if (validationMode === 'clamp') {
        const clamped = Math.min(Math.max(year || min, min), max);
        const strVal = String(clamped);
        setLocalValue(strVal);
        notifyChange(clamped);
      } else {
        setLocalValue(value != null ? String(value) : '');
        message.warning(`年份必须在${min}-${max}之间`);
      }
    } else {
      const formatted = zeroPad ? String(year).padStart(4, '0') : String(year);
      setLocalValue(formatted);
      notifyChange(year);
    }
  };

  const handleBlur = (e) => {
    validateAndFormat();
    if (onBlur) onBlur(e);
  };

  const handlePressEnter = (e) => {
    validateAndFormat();
    if (onPressEnter) onPressEnter(e);
  };

  return (
    <Input
      ref={inputRef}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onPressEnter={handlePressEnter}
      onKeyDown={onKeyDown}
      placeholder={placeholder || `请输入年份（${min}-${max}）`}
      style={style}
      maxLength={4}
      data-cell-key={dataCellKey}
      autoFocus={autoFocus}
    />
  );
};

export default YearInput;
